import { CSSProperties, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  BLACKJACK_AMERICAN_ID,
  BLACKJACK_ID,
  BLACKJACK_UNLIMITED_ID,
  CASINO_HOLDEM_ID,
  THIRTY_TWO_CARDS_ID,
  THREE_CARD_POKER_ID,
} from '@ezugi/constants';
import { Notification } from '@ezugi/primitives';
import { DeviceInfo } from '@ezugi/utils';

import { useIntl } from '../../hooks/useIntl';
import { notificationActions } from '../../store/actions';
import { gameIdSelector } from '../../store/selectors';
import { notificationSelector } from '../../store/selectors/notification';

/**
 * NOTE:
 * Games with DECISIONS have bigger <GameTools /> and as such we have to adjust the position
 * of the <GameNotifications /> component
 */

// BlackJack, TCP, CH, 32Cards
const DECISION_GAME_IDS = [
  BLACKJACK_ID,
  BLACKJACK_AMERICAN_ID,
  BLACKJACK_UNLIMITED_ID,
  THREE_CARD_POKER_ID,
  CASINO_HOLDEM_ID,
  THIRTY_TWO_CARDS_ID,
];

// gameId number / string
function getNotificationStyle(gameId?: string | number) {
  return {
    color: '#8093c0',
    ...(!DeviceInfo.isHandheld && {
      marginTop: `${DECISION_GAME_IDS.includes(Number(gameId)) ? 36 : 30}%`,
    }),
  };
}

type GameNotificationsProps = {
  className?: string;
  ignoreStyle?: boolean;
  getCustomPosition?: () => CSSProperties;
  withPortal?: boolean;
};

const GameNotifications = ({
  className,
  getCustomPosition,
  ignoreStyle = false,
  withPortal = true,
}: GameNotificationsProps) => {
  const notification = useSelector(notificationSelector);
  const gameId = useSelector(gameIdSelector);
  const dispatch = useDispatch();

  const removeNotification = useCallback(
    () => {
      dispatch(notificationActions.notification.remove());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const notificationMessage = notification?.message ?? '';

  const { message } = useIntl({
    keys: { message: notificationMessage },
    variables: { ...notification?.variables },
    fallback: { message: notificationMessage, ...notification?.fallback },
  });

  const style = useMemo(
    () => (ignoreStyle ? {} : getNotificationStyle(gameId)),
    [ gameId, ignoreStyle ]
  );

  return (
    <Notification
      className={className}
      message={message}
      on={!!notification}
      delay={notification?.delay}
      onClose={removeNotification}
      autoClose={notification?.autoClose}
      style={style}
      getCustomPosition={getCustomPosition}
      withPortal={withPortal}
    />
  );
};

export default GameNotifications;
